/**
 * @fileoverview Your Seasons: everything that happened in the garden, day by day, as
 * the backend tells it (the season brief, GET /season); this page lays it
 * out and keeps the two filters.
 *
 * Two drop-downs, Months and Beds, each taking as many answers as wanted;
 * one line naming what is shown and how much; then the days, newest first,
 * each a list of what happened in words: a plant added, a photo and the note
 * written on it, a note, a stage (in the stage's own pill), a harvest, a
 * plant taken out; and the weather's real first frost and freeze in fall and
 * last frost and freeze in spring (D58), each with the night's low under it,
 * about no plant or bed. A plant added from a photo taken on an earlier day is one row, its Added row, with that photo's
 * thumbnail and "Photo taken Oct 20" (B81; the brief folds the rest in). Plants added to one bed on one day are one row that opens
 * (the brief groups them). A kind of row this page does not know yet is drawn
 * with a plain icon, never taking the page down. Every plant's entry opens its plant's card over
 * the page; a bed's own photo or note opens the bed on Beds (its card,
 * scrolled to), or, for a bed since archived, is only words, since Beds no
 * longer shows it.
 *
 * It replaced two strips of chips that scrolled sideways, a List/Calendar
 * switch, a calendar of coloured dots and a legend to decode them, which on a
 * phone filled half the screen before the first entry and could hide the
 * month being shown. Choosing beds never moves the months: an empty choice
 * says so and offers a way back.
 *
 * Above the days: the season's header (which season, what it has done, its
 * milestones), the way into the season review once there is enough to see
 * ("Your season so far ›", then "Your 2026 season ›": SeasonReviewEntry)
 * with its once-a-season asks to share it (SeasonMoments), what is ahead
 * (the nearer frost, the planting lines, the moons, the next equinox or
 * solstice), and on day one a card that says what will fill the page and
 * offers the first entry. See SeasonHeader. All of it the brief's words.
 *
 * A tab in the bar, and reached from each bed's card on Locations.
 *
 * Where: the route /season (the Seasons tab; /seasons goes there too).
 * API: GET /season (useSeason: the header, the entry, the ask, What's
 * ahead, the beds, the diary, its months and the month to open on); the
 * plants (usePlantsContext), so a row opens its plant's card at once, and GET
 * /plants?plantObjId= (usePlantById) for a row's plant not among them (a
 * removed one), read when the row is tapped (W24 Job H: the whole archive was
 * read on every visit); the plant form's save (addPlant) on day one. Day one's "Add a plant" opens the Plants pop-up (D78 P1,
 * useAddPlant: "Take or upload a photo" or "Type it in"), and its "Take a photo" starts that photo row
 * straight away, reviewed in "Check and save" over this page.
 * Settings: ?month= (comma list, or 'all') and ?bed= (comma list) keep the
 * choice in the address; the gardener's language and unit (useSeason).
 * Words (en/es): season.* (title, lede, loading, months, beds, allMonths,
 * allBeds, allBedsLower, nMonths, nBeds, done, nothingIn, nothingInYet,
 * nothingWhen, showAllBeds, goLatest); plants.list.loadFailed,
 * plants.form.saveFailed; the rows', headings' and header's words are the
 * brief's (a frost row's title, season.kind.*, and its low, season.frost.low).
 * Watch out: with no month asked for, it opens on the month the brief names
 * (W24 Job H, from the garden's own day, never the phone's clock): openMonth,
 * the garden's month if it has something, else the latest month that does;
 * with beds chosen, the newest of those beds' months (openMonthByBed). The
 * month filter's options and their names are the brief's (months).
 * Failures: a garden or a season that could not be read because of a
 * failure on our side (D31, lib/serverFailure) says the standard sentence,
 * reported to Sentry, with the support address as a link.
 *
 * @module SeasonPage
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Archive, ArrowUpRight, Camera, ChevronDown, Circle, MapPin, NotebookPen, Snowflake, Sprout, ThermometerSnowflake, Wheat, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { usePlantById } from '@/shared/hooks/usePlantById';
import type { SeasonDiaryDay, SeasonDiaryGroup, SeasonDiaryKind, SeasonDiaryRow } from '@/shared/api/season';
import { DateTime } from '@/shared/utils/dateUtils';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { Button } from '@/shared/components/ui/button';
import { Pill } from '@/shared/components/ui/Pill';
import { MultiSelect } from '@/shared/components/ui/multi-select';
import { getStageTheme } from '@/shared/theme/stageThemes';
import { PlantQuickLook } from '@/features/plant-list/components/PlantQuickLook';
import { SeasonEmpty, SeasonHeader, WhatsAhead } from './components/SeasonHeader';
import { SeasonReviewEntry } from './components/SeasonReviewEntry';
import { SeasonMoments } from './components/SeasonMoments';
import { useSeason } from './lib/useSeason';
import { useAddPlant } from '@/features/add-by-photo/useAddPlant';
import type { PlantWritePayload } from '@/shared/api/plantCatalogue';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';

/**
 * Each kind of entry's icon. The frost rows (D58) use the plant cards' cold icons (card/Verdict): the thermometer for a
 * frost, the snowflake for a freeze.
 */
const ICONS: Record<SeasonDiaryKind, LucideIcon> = {
  added: Sprout,
  photo: Camera,
  note: NotebookPen,
  stage: ArrowUpRight,
  harvest: Wheat,
  removed: Archive,
  frostFirst: ThermometerSnowflake,
  frostLast: ThermometerSnowflake,
  freezeFirst: Snowflake,
  freezeLast: Snowflake,
};

/**
 * An entry's icon by its kind; a kind the server sends that this app does not know yet gets a plain circle, so a new
 * kind never takes the page down.
 *
 * @param kind - The row's kind, as the server sent it.
 * @returns The icon to draw.
 */
const iconOf = (kind: string): LucideIcon =>
  (Object.prototype.hasOwnProperty.call(ICONS, kind) ? ICONS[kind as SeasonDiaryKind] : Circle);

/**
 * A list param: "2026-05,2026-09" → ["2026-05", "2026-09"].
 *
 * @param raw - The parameter's value, or null when absent.
 */
const listParam = (raw: string | null): string[] => (raw ? raw.split(',').filter(Boolean) : []);

/**
 * A day's rows in the beds chosen (every row when none is): a group's rows
 * share its bed, so the group stays whole.
 *
 * @param rows - The day's rows.
 * @param bedIds - The beds chosen, as the address keeps them.
 */
const rowsInBeds = (rows: SeasonDiaryDay['rows'], bedIds: string[]): SeasonDiaryDay['rows'] =>
  (bedIds.length === 0 ? rows : rows.filter((row) => bedIds.includes(String(row.bedId))));

/** The Seasons tab: the season's header, what is ahead, and the days. */
export const SeasonPage: React.FC = () => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { plants, isLoading, error: plantsError, addPlant } = usePlantsContext();
  const { brief, loading: seasonLoading, error: seasonError } = useSeason();
  /** Saves day one's first plant from the form; a refusal is thrown for the form to say. */
  const savePlant = async (payload: PlantWritePayload): Promise<number | null> => {
    const result = await addPlant(payload);
    if (!result.success) throw new Error(result.error ?? t('plants.form.saveFailed'));
    return result.plant_obj_id != null ? Number(result.plant_obj_id) : null;
  };
  // Day one's "Add a plant": the Plants pop-up, as on the Plants tab; "Type it in" is the plant form.
  const adding = useAddPlant({ onSave: savePlant });
  const [params, setParams] = useSearchParams();
  // The plant a row opens: at once from the garden, else read by its id when tapped (a removed one).
  const plantById = usePlantById(plants);

  const diary = React.useMemo(() => brief?.diary ?? [], [brief]);
  const beds = React.useMemo(() => brief?.beds ?? [], [brief]);
  // The beds still on Beds, where a bed's own entry can take the gardener.
  const liveBeds = React.useMemo(() => new Set(brief?.liveBeds ?? []), [brief]);
  const bedIds = listParam(params.get('bed')).filter((id) => beds.some((bed) => String(bed.id) === id));
  /** The days with their rows in the beds chosen (every day when none is), empty days dropped. */
  const inBeds = React.useMemo(
    () => diary.map((day) => ({ ...day, rows: rowsInBeds(day.rows, bedIds) })).filter((day) => day.rows.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [diary, bedIds.join(',')]
  );

  // Every month from the diary's first day to the garden's month, newest first, empty ones too (so May can be looked at
  // and found empty), with their names: the brief's, from the garden's own day.
  const monthList = React.useMemo(() => brief?.months ?? [], [brief]);
  const monthKeys = React.useMemo(() => monthList.map((month) => month.key), [monthList]);
  /** The garden's month: the newest the brief lists (its openMonth when it lists none). */
  const gardenMonth = monthKeys[0] ?? brief?.openMonth ?? '';
  /**
   * The month to open on, the brief's: openMonth for the whole garden; with beds chosen, the newest of those beds' own
   * months (openMonthByBed), or the garden's month when none of them has anything.
   */
  const openMonth = bedIds.length === 0
    ? brief?.openMonth ?? gardenMonth
    : bedIds.map((id) => brief?.openMonthByBed?.[id] ?? '').reduce((newest, key) => (key > newest ? key : newest), '') || gardenMonth;

  // No months asked for: the month the brief names. "all" asks for every month.
  const askedMonths = params.get('month');
  const months = askedMonths === 'all'
    ? []
    : askedMonths
      ? listParam(askedMonths).filter((key) => monthKeys.includes(key))
      : [openMonth];

  /** The days in the beds and months chosen: what is listed below. */
  const shown = React.useMemo(
    () => (months.length === 0 ? inBeds : inBeds.filter((day) => months.includes(day.day.slice(0, 7)))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inBeds, months.join(',')]
  );

  /**
   * Chooses months or beds, in the address so the choice survives a reload.
   *
   * @param key - Which drop-down.
   * @param values - What is chosen; none is every month, or every bed.
   */
  const setList = (key: 'month' | 'bed', values: string[]): void => {
    const next = new URLSearchParams(params);
    // Choosing beds keeps the months in view, even the ones chosen for us.
    if (key === 'bed' && !next.get('month')) next.set('month', months.length ? months.join(',') : 'all');
    if (key === 'month') next.set('month', values.length ? values.join(',') : 'all');
    else if (values.length) next.set('bed', values.join(',')); else next.delete('bed');
    setParams(next, { replace: true });
  };

  /**
   * Words as a list the way the language says it: "May and September".
   *
   * @param items - The words.
   */
  const list = (items: string[]): string => {
    // Intl.ListFormat: "May and September", "mayo y septiembre". Not in this TS lib yet.
    const ListFormat = (Intl as unknown as { ListFormat?: new (lang: string, o: object) => { format: (x: string[]) => string } }).ListFormat;
    try { return ListFormat ? new ListFormat(language, { style: 'long', type: 'conjunction' }).format(items) : items.join(', '); } catch { return items.join(', '); }
  };
  /** A "YYYY-MM" month as "May 2026": the brief's name for it, in the brief's language. */
  const monthName = (key: string): string => monthList.find((month) => month.key === key)?.text ?? key;
  /** A bed's name by its id, as the address keeps it. */
  const bedName = (id: string): string => beds.find((bed) => String(bed.id) === id)?.name ?? '';

  if (isLoading || (seasonLoading && !brief)) {
    return <GrowLoader variant="screen" label={t('season.loading')} />;
  }
  // Plants that could not be read are said so, rather than offered day one's
  // empty season and its "Add your first plant"; a season that could not be
  // read likewise.
  if (plantsError && (plants ?? []).length === 0) {
    return <p role="alert" className="py-16 text-center text-sm text-destructive"><FailureText text={errorMessage(plantsError, t('plants.list.loadFailed'))} /></p>;
  }
  if (!brief) {
    return <p role="alert" className="py-16 text-center text-sm text-destructive"><FailureText text={errorMessage(seasonError, t('plants.list.loadFailed'))} /></p>;
  }

  const whenText = months.length ? list(months.map(monthName)) : t('season.allMonths');
  const whereText = bedIds.length ? list(bedIds.map(bedName)) : t('season.allBedsLower');

  return (
    <div className="mx-auto max-w-2xl p-4">
      <h1 className="text-2xl font-semibold text-foreground font-body">{t('season.title')}</h1>
      <p className="mt-0.5 text-sm text-muted-foreground">{t('season.lede')}</p>

      {diary.length === 0 ? (
        <SeasonEmpty title={brief.season.title} onAddPlant={() => adding.open()} onTakePhoto={adding.photo} />
      ) : (
        <SeasonHeader header={brief.header} onMonth={(month) => setList('month', [month])} />
      )}
      <SeasonReviewEntry offered={brief.offered} />
      <SeasonMoments due={brief.due} />
      <WhatsAhead ahead={brief.ahead} />

      {diary.length > 0 && (
        <>
          <div className={cn('mt-3 grid gap-2', beds.length > 1 ? 'grid-cols-[1.35fr_1fr]' : 'grid-cols-1')}>
            <MultiSelect
              label={t('season.months')}
              options={monthList.map((month) => ({
                value: month.key,
                label: month.text,
                short: DateTime.fromISO(`${month.key}-01`).setLocale(language).toLocaleString({ month: 'short', year: 'numeric' }),
              }))}
              value={months}
              onChange={(next) => setList('month', next)}
              allLabel={t('season.allMonths')}
              manyLabel={(count) => t('season.nMonths', { count })}
              doneLabel={t('season.done')}
            />
            {beds.length > 1 && (
              <MultiSelect
                label={t('season.beds')}
                options={beds.map((bed) => ({ value: String(bed.id), label: bed.name }))}
                value={bedIds}
                onChange={(next) => setList('bed', next)}
                allLabel={t('season.allBeds')}
                manyLabel={(count) => t('season.nBeds', { count })}
                doneLabel={t('season.done')}
                align="end"
              />
            )}
          </div>
          {shown.length === 0 ? (
            <div className="rounded-lg border bg-card p-6 text-center">
              <p className="mb-3 font-medium">
                {bedIds.length
                  ? months.length
                    ? t('season.nothingIn', { place: whereText, when: whenText })
                    : t('season.nothingInYet', { place: whereText })
                  : t('season.nothingWhen', { when: whenText })}
              </p>
              {bedIds.length ? (
                <Button variant="outline" onClick={() => setList('bed', [])}>{t('season.showAllBeds')}</Button>
              ) : (
                <Button variant="outline" onClick={() => setList('month', [inBeds[0]?.day.slice(0, 7) ?? gardenMonth])}>{t('season.goLatest')}</Button>
              )}
            </div>
          ) : (
            <div className="mt-3"><EventDays days={shown} showBed={bedIds.length !== 1} onOpen={plantById.open} liveBeds={liveBeds} /></div>
          )}
        </>
      )}

      <PlantQuickLook plant={plantById.plant} onClose={plantById.close} />
      {adding.element}
    </div>
  );
};

/** What the days are drawn from, and what an entry opens. */
interface EventDaysProps {
  /** The days shown, newest first, each with its rows. */
  days: SeasonDiaryDay[];
  /** Whether each row names its bed (not when one bed is chosen). */
  showBed: boolean;
  /** Opens a plant's card over the page. */
  onOpen: (plantObjId: number) => void;
  /** The beds still on Beds, by id, where a bed's own entry leads. */
  liveBeds: ReadonlySet<number>;
}

/**
 * The days, newest first, each one list of rows.
 *
 * @param props - EventDaysProps.
 */
const EventDays: React.FC<EventDaysProps> = ({ days, showBed, onOpen, liveBeds }) => (
  <div className="space-y-4">
    {days.map((day) => (
      <section key={day.day}>
        <h2 className="font-body mb-1.5 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{day.title.text}</h2>
        <div className="divide-y overflow-hidden rounded-lg border bg-card">
          {day.rows.map((row) => (row.kind === 'addedGroup'
            ? <AddedGroup key={row.key} group={row} showBed={showBed} onOpen={onOpen} />
            : <EventRow key={row.key} event={row} showBed={showBed} onOpen={onOpen} liveBeds={liveBeds} />))}
        </div>
      </section>
    ))}
  </div>
);

/** An entry's row, a thumb tall. */
const ROW_SHAPE = 'flex w-full min-h-11 items-start gap-3 px-3 py-2.5 text-left';
/** An entry's row that opens something: the whole row is the button or link. */
const ROW = `${ROW_SHAPE} transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring`;

/**
 * The bed, with the pin the plant cards use.
 *
 * @param props.name - The bed's name.
 */
const BedLine: React.FC<{ name: string }> = ({ name }) => (
  <span className="mt-0.5 flex items-center gap-0.5 text-sm font-medium">
    <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
    <span className="truncate">{name}</span>
  </span>
);

/** One entry, what it opens, and where it sits. */
interface EventRowProps {
  /** The entry. */
  event: SeasonDiaryRow;
  /** Whether to name its bed under it. */
  showBed: boolean;
  /** Opens a plant's card over the page. */
  onOpen: (plantObjId: number) => void;
  /** The beds still on Beds, by id; a bed's own entry leads there. None given, it leads nowhere. */
  liveBeds?: ReadonlySet<number>;
  /** Indented under a group's row ("7 plants added"). */
  inset?: boolean;
}

/**
 * One entry: who, what, the words written, the bed, and a photo's thumbnail (a photo row's, or, for a plant added from an
 * older photo, its Added row's, with "Photo taken" the day: B81).
 *
 * A plant's entry opens its plant's card. A bed's own photo or note opens
 * the bed on Beds, where its card shows the same photos and notes; a bed
 * since archived is not on Beds, so its entry is only words.
 *
 * @param props - EventRowProps.
 */
const EventRow: React.FC<EventRowProps> = ({ event, showBed, onOpen, liveBeds, inset }) => {
  /** The entry's icon, by its kind (a plain one for a kind not known here). */
  const Icon = iconOf(event.kind);
  const theme = event.kind === 'stage' ? getStageTheme((event.stageId ?? 0) as Parameters<typeof getStageTheme>[0]) : null;
  const plantObjId = event.plantObjId;
  const bedId = plantObjId === null && event.bedId !== null && liveBeds?.has(event.bedId) ? event.bedId : null;
  /** The photo's picture: its full signed link, as the plant cards show a thumb, else its thumbnail. */
  const photoSrc = event.photo ? (event.photo.url || event.photo.thumbUrl || '') : '';
  /** The row's inside, put in a button (a plant's), a link (a live bed's) or a plain row. */
  const inner = (
    <>
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1 text-[0.9375rem] leading-snug">
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {/* A frost row is about no plant or bed: its title alone, with no name before it. */}
          {event.plantName ? (
            <>
              <span className="font-semibold">{event.plantName}</span>
              <span>· {event.text}</span>
            </>
          ) : (
            <span className="font-semibold">{event.text}</span>
          )}
          {theme && (
            <Pill size="sm" theme={theme}>{event.stageName}</Pill>
          )}
          {/* Grow's automatic stage, not one recorded. */}
          {event.estimated && <span className="text-xs text-muted-foreground">{event.estimated.text}</span>}
        </span>
        {/* A frost row's night low, as the server said it ("Low 31°F"). */}
        {event.low?.text && <span className="mt-0.5 block text-sm text-muted-foreground">{event.low.text}</span>}
        {/* The note written on the photo, or the note itself. */}
        {event.note && <span className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{event.note}</span>}
        {/* An Added row for a plant added from an older photo (B81): the day that photo was taken, the server's words. */}
        {event.photoTaken?.text && <span className="mt-0.5 block text-sm text-muted-foreground">{event.photoTaken.text}</span>}
        {/* A bed's own entry is already named for the bed. */}
        {showBed && plantObjId !== null && <BedLine name={event.bedName} />}
      </span>
      {(event.kind === 'photo' || event.kind === 'added') && photoSrc && (
        <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-md"><FramedPhoto src={photoSrc} alt="" loading="lazy" /></span>
      )}
    </>
  );
  if (plantObjId !== null) {
    return <button type="button" onClick={() => onOpen(plantObjId)} className={cn(ROW, inset && 'pl-11')}>{inner}</button>;
  }
  if (bedId !== null) {
    return <Link to={`/beds/${bedId}`} className={cn(ROW, inset && 'pl-11')}>{inner}</Link>;
  }
  return <div className={cn(ROW_SHAPE, inset && 'pl-11')}>{inner}</div>;
};

/** A day's plants added to one bed, and what each opens. */
interface AddedGroupProps {
  /** The plants added, several at once, all to one bed on one day. */
  group: SeasonDiaryGroup;
  /** Whether to name the bed under the group's row. */
  showBed: boolean;
  /** Opens a plant's card over the page. */
  onOpen: (plantObjId: number) => void;
}

/**
 * "7 plants added", opening in place to the seven.
 *
 * @param props - AddedGroupProps.
 */
const AddedGroup: React.FC<AddedGroupProps> = ({ group, showBed, onOpen }) => {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  return (
    <div>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((was) => !was)} className={ROW}>
        <Sprout className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1 text-[0.9375rem] leading-snug">
          <span className="font-semibold">{group.text}</span>
          {showBed && <BedLine name={group.bedName} />}
        </span>
        <ChevronDown className={cn('mt-0.5 h-5 w-5 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none', open && 'rotate-180')} aria-hidden="true" />
      </button>
      <div id={id} hidden={!open} className="divide-y border-t">
        {group.rows.map((event) => <EventRow key={event.key} event={event} showBed={false} onOpen={onOpen} inset />)}
      </div>
    </div>
  );
};
